// NOTICE: KAPTION-INTEGRATION
// Pure helpers for the contact-first "Nuevo mensaje" flow
// (KAPTION_CONTACT_FIRST_CONVERSATION).
// Spec: features/kaption/contact-first-conversation/contact-first-conversation.feature.yaml

export const CONTACT_FIRST_FLAG = 'KAPTION_CONTACT_FIRST_CONVERSATION';
export const REPLY_TEMPLATE_FLAG = 'KAPTION_REPLYBOX_TEMPLATE_PICKER';
export const LAST_INBOX_STORAGE_KEY = 'kaption_contact_first_last_inbox';
export const DRAFT_ROUTE_NAME = 'kaption_contact_first_draft';

const STATUS_RANK = { open: 0, pending: 1, snoozed: 2, resolved: 3 };

const inboxIdOf = conversation =>
  Number(conversation?.inbox_id ?? conversation?.inboxId);

/**
 * contact-first-conversation.RESOLVE.1 — the contact's conversation in that
 * inbox, if any: an open one first, then the most recent activity. Never a new
 * one when one exists, so no duplicates.
 */
export const pickExistingConversation = (conversations, inboxId) => {
  const inThisInbox = (conversations || []).filter(
    conversation => inboxIdOf(conversation) === Number(inboxId)
  );
  if (!inThisInbox.length) return null;
  const activity = c =>
    Number(c.last_activity_at ?? c.lastActivityAt ?? c.timestamp ?? 0);
  return [...inThisInbox].sort((a, b) => {
    const rank = (STATUS_RANK[a.status] ?? 4) - (STATUS_RANK[b.status] ?? 4);
    return rank || activity(b) - activity(a);
  })[0];
};

/**
 * contact-first-conversation.INBOX.1 — the default WhatsApp inbox:
 * the contact's own (most recent conversation), then the last one used here,
 * then the account's preferred one, then the first.
 */
export const resolveDefaultInbox = ({
  inboxes = [],
  contactConversations = [],
  lastUsedInboxId = null,
  preferredInboxId = null,
}) => {
  if (!inboxes.length) return null;
  const byId = id => inboxes.find(inbox => inbox.id === Number(id));
  const recent = [...(contactConversations || [])]
    .filter(conversation => byId(inboxIdOf(conversation)))
    .sort(
      (a, b) =>
        Number(b.last_activity_at ?? b.timestamp ?? 0) -
        Number(a.last_activity_at ?? a.timestamp ?? 0)
    )[0];
  return (
    (recent && byId(inboxIdOf(recent))) ||
    (lastUsedInboxId && byId(lastUsedInboxId)) ||
    (preferredInboxId && byId(preferredInboxId)) ||
    inboxes[0]
  );
};

/** Persisted per browser only: a convenience, never shared state. */
export const readLastUsedInboxId = () => {
  try {
    return Number(window.localStorage.getItem(LAST_INBOX_STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};

export const writeLastUsedInboxId = inboxId => {
  try {
    window.localStorage.setItem(LAST_INBOX_STORAGE_KEY, String(inboxId));
  } catch {
    // storage unavailable (private window): the default simply falls back
  }
};

/**
 * contact-first-conversation.SEARCH.1 — only contacts WhatsApp can reach: a
 * phone number is required.
 */
export const reachableContacts = (contacts = []) =>
  (contacts || []).filter(contact => contact?.phoneNumber);

/** Digits-only comparison of two phone numbers. */
export const samePhone = (a, b) => {
  const digits = value => String(value || '').replace(/\D/g, '');
  return Boolean(digits(a)) && digits(a) === digits(b);
};

/** WhatsApp source_id fallback for a phone: E.164 without the plus. */
export const phoneToSourceId = phone => String(phone || '').replace(/^\+/, '');
